import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const cogolinEn: ChauffeurPage = {
  slug: "cogolin",
  title: "VTC & private chauffeur in Cogolin",
  subtitle:
    "Private chauffeur in Cogolin: the gateway to the Gulf of Saint-Tropez and its marina, Nice airport transfers and hourly hire.",
  keywords: [
    "vtc cogolin",
    "private chauffeur cogolin",
    "cogolin airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Cogolin",
    subtitle:
      "The gateway to the Gulf of Saint-Tropez and its marina: Nice airport transfer, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC Cogolin",
      "Marines de Cogolin chauffeur",
      "Nice airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Cogolin (Gulf of Saint-Tropez)",
    badges: [
      "Marines de Cogolin marina",
      "Nice airport transfer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Cogolin",
    items: [
      {
        title: "Gateway to the gulf",
        description:
          "A crossroads between sea and hinterland: a handy starting point for Saint-Tropez, Grimaud and the beaches.",
      },
      {
        title: "Marines de Cogolin",
        description:
          "Service to the marina’s quays and pontoons, for owners, crews and guests.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, around 1h20 to 1h30 of road handled in high-end comfort.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Cogolin",
    services: [
      {
        title: "Nice airport transfer",
        text: "Cogolin ⇄ Nice Côte d’Azur airport with flight tracking; a longer run than from Cannes, timed at departure.",
      },
      {
        title: "Saint-Raphaël TGV station",
        text: "Pick-up at Saint-Raphaël station, around 30 to 40 minutes away, as a rail alternative.",
      },
      {
        title: "Marines de Cogolin",
        text: "Transfers to the marina and its pontoons, coordinated with the concierge and crews.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day for the village, the gulf’s beaches and Saint-Tropez.",
      },
      {
        title: "Long distance",
        text: "Trips of any distance from Cogolin: Cannes, Nice, Monaco and beyond, with a dedicated chauffeur.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old village of Cogolin",
        description: "Provençal heart, town-hall square and craft workshops.",
      },
      {
        name: "Marines de Cogolin",
        description: "Marina with residential quays and pontoons.",
      },
      {
        name: "Local manufactures (carpets & pipes)",
        description: "Cogolin’s carpet and briar-pipe traditions.",
      },
      {
        name: "Saint-Tropez",
        description: "Neighbouring port and village, a few minutes away.",
      },
      {
        name: "Saint-Raphaël station",
        description: "TGV station around 30-40 min away, a transfer point.",
      },
      {
        name: "Nice airport",
        description: "Nice Côte d’Azur airport, around 1h20-1h30 away.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur for Cogolin",
    subheading:
      "From the gateway of the gulf to the Marines de Cogolin, a premium service towards Saint-Tropez and the beaches.",
    paragraphs: [
      "Cogolin acts as the gateway to the Gulf of Saint-Tropez: a lively Provençal town, known for its carpet manufactures and briar pipes, it links the hinterland to the sea and to the Marines de Cogolin, its marina. It makes a practical base from which to reach Saint-Tropez, Grimaud and the beaches.",
      "Like the whole gulf, Cogolin has no airport: arrivals mostly come through Nice Côte d’Azur, around 1h20 to 1h30 away depending on summer traffic. We set the departure time to limit waiting, and offer Saint-Raphaël’s TGV station, some thirty minutes away, as an alternative transfer point.",
      "For marina residents, crews and villa guests, one chauffeur handles transfers and hourly hire, with discretion and flexible timing in high season. Fixed fares agreed in advance or by the hour: an immediate quote.",
    ],
    bullets: [
      "Service to the Marines de Cogolin",
      "Nice airport & Saint-Raphaël station transfers",
      "A base towards Saint-Tropez",
    ],
  },
  process: {
    heading: "Book a chauffeur in Cogolin",
    steps: [
      "Tell us your route (village, marina, airport, station) and times.",
      "We recommend the right vehicle and fare.",
      "The chauffeur tracks your flight or train and adjusts departure.",
      "Direct transfer or hourly hire across the gulf during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Cogolin"),
    {
      q: "How long is the drive to Nice airport from Cogolin?",
      a: "Around 1h20 to 1h30 outside peak times, more on summer weekends and traffic peaks in the gulf. We adjust the departure and track your flight.",
    },
    {
      q: "Do you serve Saint-Raphaël TGV station?",
      a: "Yes. Saint-Raphaël station is around 30 to 40 minutes from Cogolin; it is a handy alternative transfer point for train arrivals and departures.",
    },
    {
      q: "Do you handle the link with Saint-Tropez?",
      a: "Yes, Saint-Tropez is a few minutes away; we run round trips to the port, restaurants and nights out, with safe late-night returns.",
    },
    {
      q: "How far ahead should I book?",
      a: "In high summer season, book as early as possible: demand is strong across the Gulf of Saint-Tropez. We also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele and villa stays.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes sedan, premium van (V-Class), SUV and minibus depending on group size and luggage, suited to the long run from Nice or Cannes.",
    },
    {
      q: "Do you serve the Marines de Cogolin?",
      a: "Yes, we run transfers to the marina, its quays and pontoons, for owners, crews and their guests, coordinated with the concierge.",
    },
  ],
  relatedLinks: [
    {
      label: "Airport transfers in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Chauffeur service Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Chauffeur hire on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Vehicle rental in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Cogolin",
    text: "Nice airport transfer, Saint-Raphaël station or the Marines de Cogolin: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Cogolin — private chauffeur & transfers",
    description:
      "Private car service in Cogolin: Nice airport transfer, Saint-Raphaël TGV station, the Marines de Cogolin marina and the Saint-Tropez link.",
    canonical: "/en/chauffeur/cogolin",
  },
};

export default cogolinEn;
